import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiButton, Box, Button, ContentTemplate, SectionHeader, Typography } from 'web-library';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { Theme as AugmentedTheme, useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import { useGetProfileAmendmentRequestFileConfigs } from '../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequestFileConfigs';
import { prepareFiles } from '../../../utils/UploadUtil';
import { useHandleSubmitForApprovalWithFileUpload } from '../logic/useCandidateProfileRequestForAmendmentDetails';
import { usePutAddAdditionalDocuments } from '../../../api-refactored/profile-amendment-requests/usePutAddAdditionalDocuments';
import RouteKey from '../../../routes/key';

const { profile } = RouteKey.child;
const { candidateDetail } = RouteKey.profile.child;

const useStyles = makeStyles()((theme) => ({
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    color: theme.color.text.textHeader,
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  proceedButton: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(2),
    },
  },
}));

const UploadSupportingDocumentsContent = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();
  const { uuid } = useParams<{ uuid: string }>();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const { data: profileAmendmentRequestFileConfigs } = useGetProfileAmendmentRequestFileConfigs();

  const { mutate: mutatePutAddAdditionalDocuments } = usePutAddAdditionalDocuments({
    onSuccess: () => {
      setApiButtonStatus('success');
      handleGoBackToProfile();
    },
    onError: (err) => {
      setApiButtonStatus('error');
      setUploadFileErrorMessage(err?.response?.data?.message!);
    },
    uuid,
  });
  const {
    mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles,
  } = useHandleSubmitForApprovalWithFileUpload({
    setApiButtonStatus,
    setErrorMessage: setUploadFileErrorMessage,
    setUploadFileErrorMessage,
    files,
    setFiles,
  });

  const handleGoBackToProfile = useCallback(
    () => history.push(`/${profile}/${candidateDetail.route}`),
    [history]
  );

  const handleUploadSupportingDocuments = useCallback(() => {
    if (!files.length) {
      setUploadFileErrorMessage(
        t('profile->>CreateCandidateForm.MissingSupportingDocumentMessage')
      );
    } else {
      const { filenames } = prepareFiles(files, setFiles);
      setShouldSubmit(true);
      setApiButtonStatus('loading');

      if (filenames.length) {
        mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles({
          filenames,
          uuid,
        });
      }
    }
  }, [files, mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles, t, uuid]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.some((file) => file.hasError);

    const fileKeys: string[] = [];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit) {
      mutatePutAddAdditionalDocuments({
        fileKeys,
      });
      setShouldSubmit(false);
    }
  }, [files, handleGoBackToProfile, mutatePutAddAdditionalDocuments, shouldSubmit]);

  return (
    <ContentTemplate>
      <SectionHeader title={t('exam->>UploadDocument.UploadSupportingDocuments')} />
      {profileAmendmentRequestFileConfigs && (
        <RequestFileDropZone
          fileConfig={profileAmendmentRequestFileConfigs}
          files={files}
          setFiles={setFiles}
          isLoading={apiButtonStatus === 'loading'}
          isEditable
        />
      )}
      {uploadFileErrorMessage && (
        <Typography className={classes.errorText} variant="body2">
          {uploadFileErrorMessage}
        </Typography>
      )}
      <Box className={`${classes.responsiveFlexContainer} ${classes.buttonContainer}`}>
        <Button
          id="button-upload-supporting-documents-cancel"
          variant="outlined"
          fullWidth={isMobile}
          onClick={handleGoBackToProfile}
          disabled={apiButtonStatus === 'loading'}
        >
          {t('profile->>ProfileAmendmentRequests.Cancel')}
        </Button>
        <ApiButton
          className={classes.proceedButton}
          id="button-upload-supporting-documents-proceed"
          status={apiButtonStatus}
          fullWidth={isMobile}
          onClick={handleUploadSupportingDocuments}
        >
          {t('profile->>CreateCandidateForm.Proceed')}
        </ApiButton>
      </Box>
    </ContentTemplate>
  );
};

export default UploadSupportingDocumentsContent;
